import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic meta tags and structured data
 * Optimized for Morocco and multilingual support
 */
export const SEO = ({
  title = 'RAQIB - Healthcare Clinic Management System for Morocco',
  description = 'RAQIB is the leading healthcare clinic management platform in Morocco. Manage patients, appointments, medical records, and billing securely. HIPAA compliant with enterprise-grade encryption.',
  keywords = 'healthcare management Morocco, clinic management system, medical records, patient scheduling',
  image = 'https://www.raqibapp.com/og-image.png',
  url = 'https://www.raqibapp.com',
  type = 'website',
  author = 'RAQIB Inc.',
  language = 'en',
  structuredData = null,
}) => {
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={language} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="English, French, Arabic" />
      
      {/* Geographic targeting for Morocco */}
      <meta name="geo.placename" content="Morocco" />
      <meta name="geo.region" content="MA" />
      <meta name="ICBM" content="31.7917, -7.0926" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="RAQIB" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="ar_SA" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      <meta property="twitter:site" content="@RAQIBApp" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Organization Schema for Morocco
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'RAQIB Inc.',
  'description': 'Professional healthcare clinic management platform serving Morocco and beyond',
  'url': 'https://www.raqibapp.com',
  'logo': 'https://www.raqibapp.com/logo.png',
  'sameAs': [
    'https://www.facebook.com/raqibapp',
    'https://www.twitter.com/raqibapp',
    'https://www.linkedin.com/company/raqib'
  ],
  'foundingDate': '2024',
  'areaServed': {
    '@type': 'Country',
    'name': 'Morocco'
  },
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': '123 Tech Boulevard, Suite 456',
    'addressLocality': 'San Francisco',
    'addressRegion': 'CA',
    'postalCode': '12345',
    'addressCountry': 'US'
  },
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'Customer Service',
    'telephone': '+1-321-221-231',
    'email': 'info@raqibapp.com'
  }
};

/**
 * SoftwareApplication Schema - Healthcare
 */
export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  'name': 'RAQIB',
  'description': 'Healthcare clinic management platform with patient management, appointment scheduling, medical records, and secure billing.',
  'applicationCategory': 'MedicalApplication',
  'operatingSystem': 'Web',
  'url': 'https://www.raqibapp.com',
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '4.8',
    'ratingCount': '250'
  },
  'offers': [
    {
      '@type': 'Offer',
      'priceCurrency': 'USD',
      'price': '0',
      'name': 'Free Plan'
    },
    {
      '@type': 'Offer',
      'priceCurrency': 'USD',
      'price': '49',
      'name': 'Professional Plan'
    }
  ]
};

/**
 * FAQ Schema for SEO
 */
export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': [
    {
      '@type': 'Question',
      'name': 'What makes RAQIB different from other healthcare apps?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'RAQIB is specifically designed for healthcare clinics with features like patient management, appointment scheduling, medical records, and secure data handling, combined with enterprise-grade security and ease of use.'
      }
    },
    {
      '@type': 'Question',
      'name': 'How secure are my conversations?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Your data is protected with military-grade encryption (AES-256), multi-factor authentication, and HIPAA-compliant architecture. All communications are end-to-end encrypted.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Can I change my plan anytime?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes, you can upgrade or downgrade your plan at any time with no penalties. Changes take effect at the next billing cycle.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Is there a free trial available?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes! Our Free plan lets you test all core features with 2 users and 10 patients. No credit card needed to start.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Is RAQIB available in Morocco?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes, RAQIB is available and optimized for healthcare providers in Morocco with full multilingual support in Arabic, French, and English.'
      }
    }
  ]
};

/**
 * LocalBusiness Schema for Granada/Morocco targeting
 */
export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://www.raqibapp.com',
  'name': 'RAQIB',
  'description': 'Healthcare Clinic Management System serving Morocco',
  'image': 'https://www.raqibapp.com/logo.png',
  'url': 'https://www.raqibapp.com',
  'address': {
    '@type': 'PostalAddress',
    'addressCountry': 'MA'
  },
  'areaServed': {
    '@type': 'Country',
    'name': 'Morocco'
  },
  'telephone': '+1-321-221-231',
  'email': 'info@raqibapp.com'
};

/**
 * Breadcrumb Schema for navigation
 */
export const breadcrumbSchema = (breadcrumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': breadcrumbs.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': item.url
  }))
});

export default SEO;
